import type { Bank, CreditCard } from '@eti/schema-types';
import type { useTranslation } from '@eti/providers';
import { paymentMethodCodes, paymentMethodTypes } from '../constants/paymentMethods';
import type {
  BankPaymentMethodOnPaymentPage,
  CardPaymentMethodOnPaymentPage,
  PayInternalPaymentMethodOnPaymentPage,
  PaymentMethodOnPaymentPage,
  TransformedPaymentMethod,
  TransformedPaymentMethodPrice,
  TransformedWalletPaymentMethod,
  WalletOnPaymentPage,
  WalletPaymentMethodOnPaymentPage,
} from '../models';
import { KLARNA } from '../payment-methods/klarna/constants';

declare const window: Window & { ApplePaySession?: { canMakePayments: () => boolean } };

const { BANK, CARD, PAY_INTERNAL, WALLET } = paymentMethodTypes;
const {
  APPLE_PAY,
  APP_CARD,
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
  UPI,
} = paymentMethodCodes;

type PaymentMethodCodes = typeof paymentMethodCodes;
type PaymentMethodCode = PaymentMethodCodes[keyof PaymentMethodCodes];

const separateWallets = (wallets: WalletOnPaymentPage[]) =>
  wallets.reduce<{
    upiWallets: WalletOnPaymentPage[];
    otherWallets: WalletOnPaymentPage[];
    inicisWallets: WalletOnPaymentPage[];
  }>(
    (acc, wallet) => {
      if (wallet.code.match(/upi/gi)) {
        return { ...acc, upiWallets: [...acc.upiWallets, wallet] };
      }
      if (wallet.code.match(/appcard/gi)) {
        return { ...acc, inicisWallets: [...acc.inicisWallets, wallet] };
      }

      return { ...acc, otherWallets: [...acc.otherWallets, wallet] };
    },
    { upiWallets: [], otherWallets: [], inicisWallets: [] },
  );

const getAllEnabledWallets = (wallets: WalletOnPaymentPage[]) => {
  return wallets.filter(
    ({ code }) => code !== APPLE_PAY || window.ApplePaySession?.canMakePayments(),
  );
};

const getWallets = (wallets?: (WalletOnPaymentPage | null)[] | null): WalletOnPaymentPage[] =>
  wallets?.filter((wallet): wallet is WalletOnPaymentPage => Boolean(wallet)) ?? [];

const isBankPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is BankPaymentMethodOnPaymentPage => method.__typename === 'BankPaymentMethod';

const isWalletPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is WalletPaymentMethodOnPaymentPage => method.__typename === 'WalletPaymentMethod';

const isCardPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is CardPaymentMethodOnPaymentPage => method.__typename === 'CardPaymentMethod';

const isPayInternalPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is PayInternalPaymentMethodOnPaymentPage =>
  method.__typename === 'PayInternalPaymentMethod';

export const transformPaymentMethods = (paymentMethods: PaymentMethodOnPaymentPage[]) => {
  if (paymentMethods.length === 0) {
    return [];
  }
  return paymentMethods.reduce<TransformedPaymentMethod[]>((accumulatedMethods, method) => {
    if (isBankPaymentMethod(method)) {
      if (method.banks) {
        const transformedBank = method.banks
          .filter((bank): bank is Bank => Boolean(bank))
          .map((bank) => ({
            ...bank,
            paymentServiceProvider: method.paymentServiceProvider,
            texts: method.texts,
            type: BANK,
          }));

        return [...accumulatedMethods, ...transformedBank];
      }

      return accumulatedMethods;
    }

    if (isWalletPaymentMethod(method)) {
      const wallets = getAllEnabledWallets(getWallets(method.wallets ?? []));
      const { upiWallets, otherWallets, inicisWallets } = separateWallets(wallets);
      const transformedUpiWallet =
        upiWallets.length > 0
          ? {
              code: UPI,
              name: UPI,
              type: WALLET,
              options: upiWallets.map((wallet) => ({
                type: WALLET,
                ...wallet,
              })),
              paymentServiceProvider: method.paymentServiceProvider,
            }
          : null;

      const transformedOtherWallets = otherWallets.map((wallet) => ({
        ...wallet,
        type: WALLET,
        code: wallet.code as TransformedWalletPaymentMethod['code'], // wallet.code is typed as string but should ideally be an enum.
        paymentServiceProvider: method.paymentServiceProvider,
      }));

      const transformedInicisWallets =
        inicisWallets.length > 0
          ? {
              code: APP_CARD,
              name: APP_CARD,
              type: WALLET,
              options: inicisWallets.map((wallet) => ({
                ...wallet,
                type: WALLET,
                code: wallet.code as TransformedWalletPaymentMethod['code'],
              })),
              paymentServiceProvider: method.paymentServiceProvider,
            }
          : null;

      return [
        ...accumulatedMethods,
        ...transformedOtherWallets,
        ...(transformedUpiWallet ? [transformedUpiWallet] : []),
        ...(transformedInicisWallets ? [transformedInicisWallets] : []),
      ];
    }

    if (isCardPaymentMethod(method)) {
      const creditCards =
        method.creditCards?.filter((creditCard): creditCard is CreditCard => Boolean(creditCard)) ??
        [];
      const transformedCard = {
        ...method,
        creditCards,
        code: CARD,
        type: CARD,
      };
      return [...accumulatedMethods, transformedCard];
    }

    if (isPayInternalPaymentMethod(method)) {
      const transformedPayInternal = {
        ...method,
        code: PAY_INTERNAL,
        type: PAY_INTERNAL,
      };
      return [...accumulatedMethods, transformedPayInternal];
    }

    return accumulatedMethods;
  }, []);
};

export const sortPaymentMethods = (
  paymentMethods: TransformedPaymentMethod[],
  displayOrder?: string,
) => {
  if (!displayOrder) {
    return paymentMethods;
  }

  const order = displayOrder
    .split(',')
    .filter(Boolean)
    .map((code) => code.trim());

  return paymentMethods.sort((a, b) => {
    if (order.indexOf(a.code) === -1) {
      return 1;
    }
    if (order.indexOf(b.code) === -1) {
      return -1;
    }
    return order.indexOf(a.code) - order.indexOf(b.code);
  });
};

export const getPaymentMethodPrices = (
  paymentMethods: TransformedPaymentMethod[],
  t: ReturnType<typeof useTranslation>['t'],
) =>
  paymentMethods.reduce((acc: TransformedPaymentMethodPrice[], method) => {
    if (method.type === CARD) {
      return [
        ...acc,
        ...method.creditCards.map((card) => ({
          id: card.code,
          name: card.name,
          price: card.price.value,
          serviceFee: card.serviceFee?.value,
          type: method.type,
        })),
      ];
    }

    if (method.type === WALLET && (method.code === UPI || method.code === APP_CARD)) {
      return [
        ...acc,
        ...method.options.map((option) => ({
          id: option.code,
          name: t(`Payment.Method.${option.code}.Text`) || option.name,
          price: option.price.value,
          serviceFee: option.serviceFee?.value,
          type: method.type,
        })),
      ];
    }

    if (method.type === PAY_INTERNAL) {
      return [...acc];
    }

    return [
      ...acc,
      {
        id: method.code,
        name: method.name,
        price: method.price.value,
        serviceFee: method.serviceFee?.value,
        type: method.type,
      },
    ];
  }, []);

export const getMethod = (
  transformedPaymentMethods: TransformedPaymentMethod[],
  code: PaymentMethodCode,
) => transformedPaymentMethods.find((method) => method.code === code);

export const getPaymentMethodLogoId = (
  code: (typeof paymentMethodCodes)[keyof typeof paymentMethodCodes],
  shouldShowBancontactLogo: boolean = false,
) => {
  switch (code) {
    case CARD:
      return ['logo_VI', 'logo_CA', ...(shouldShowBancontactLogo ? ['logo_BT'] : [])];
    case KLARNA_ONE:
    case KLARNA_PAY_NOW:
    case KLARNA_PAY_LATER:
    case KLARNA_SLICE_IT:
    case KLARNA_SOFORT:
      return KLARNA;
    default:
      return code.toLowerCase();
  }
};
